<script>
  import { createNamespacedHelpers } from 'vuex';
  import { eventBus } from '../../main';

  const { mapGetters } = createNamespacedHelpers('plate');

  export default {
    name: 'InputDuplicate',
    props: {
      /** the currently selected letter style */
      letterStyle: {
        type: Object,
        required: true,
      },
      /** the current line of text the user is typing on */
      lineOfText: {
        type: Number,
        default: 1
      },
      /** the horizontal space allowed for the line */
      space: {
        type: Number,
        required: true
      }
    },
    data() {
      return {
        letterString: '',
        duplicateLetterString: '',
        newString: '',
        variant: {
          /** the space is close to perfect (14) and should be considered in products space per line of text */
          space: this.space
        },
        bundleData: {
          /**
           * bundles necessary product properties for other components use
           * textArray - the array containing multiple line of texts
           */
          textArray: [],
        },
        error: false,
      };
    },
    watch: {
      letterString(text) {
        this.duplicateLetterString = text.toUpperCase();
        this.processInput(text.toUpperCase());
      },
      letterStyle() {
        /**
         * when letterStyle changes ensure to prune text
         * for available characters and symbols
         */
        this.bundleTextArray(this.duplicateLetterString);
      },
    },
    computed: {
      ...mapGetters([
        'getTextArray',
        'getSelectedVariant',
        'getInputErrorArray'
      ]),
      totalPrice() {
        /**
         * Adds baseproduct price (frame) to total price for letters in bundleText
         */
        let totalPrice = 0;
        let totalLetterPrice = 0;
        let stringArr = this.letterString.split('');
        for (let i = 0; i < stringArr.length; i++) {
          let charProduct = this.getProductByChar(
            stringArr[i],
            this.letterStyle.chars
          );
          if (charProduct) {
            totalLetterPrice += parseFloat(charProduct.price);
          }
        }
        totalPrice = this.getSelectedVariant.price + totalLetterPrice;
        return totalPrice.toFixed(2);
      },
      index() {
        // ensuring texts are assigned to the right index in the array
        return this.lineOfText - 1;
      },
      /** Returns the length of letter string excluding space characters */
      getCurrentLetterStringLength() {
        const letterString = this.letterString;
        return letterString.length > 0 ? letterString.replace(/ /g,'').length : 0;
      },
    },
    methods: {
      processInput(text) {
        this.letterString = text;
        let textArray = text.split('');
        let char = textArray[textArray.length - 1];

        if (char === ' ') {
          if (this.getPlateStringWidth() <= this.variant.space) {
            // bundle text into array[lineOfText] and store in vuex
            this.bundleTextArray(text);
          } else {
            // remove character from text
            this.removeChar(textArray, false);
            this.error = true;
          }
        } else {

          const productChar = this.getProductByChar(char, this.letterStyle.chars);

          if (productChar) {
            if (this.getPlateStringWidth() <= this.variant.space) {
              // bundle text into array[lineOfText] and store in vuex
              this.bundleTextArray(text);
            } else {
              // remove character from text
              this.removeChar(textArray, false);
              this.error = true;
            }
          } else {
            this.removeChar(textArray, false);
          }
        }
      },
      checkForErrors() {
        /** checks for errors in various lines of text */
        let error = false;
        this.getInputErrorArray.forEach((errorVal) => {
          if (errorVal){
            error = errorVal;
          }
        });
        return error;
      },
      removeChar(textArray, index) {
        let string = '';
        if (index) {
          textArray.splice(index, 1);
        } else {
          textArray.pop();
        }
        textArray.forEach((char) => {
          string += char;
        });
        this.letterString = string;
      },
      getProductByChar(char, array) {
        let product = null;
        const charProduct = array.filter((l) => l.key === char);
        if (charProduct.length) {
          product = charProduct[0];
        }
        return product;
      },
      getPlateStringWidth() {
        let stringWidth = 0;
        let stringArr = this.letterString.split('');
        for (let i = 0; i < stringArr.length; i++) {
          if (stringArr[i] !== ' ') {
            let charProduct = this.getProductByChar(stringArr[i], this.letterStyle.chars);
            if (charProduct) {
              stringWidth += parseFloat(charProduct.width);
            }
          }
        }
        return stringWidth;
      },
      bundleTextArray(text) {
        let textArray = [];
        let index = this.index;
        let stringWidth = 0;
        let stringArr = text.split('');

        textArray[index] = '';

        for (let i = 0; i < stringArr.length; i++) {
           if(stringArr[i] !== ' '){
            let charProduct = this.getProductByChar(stringArr[i], this.letterStyle.chars);
            if (!charProduct) {
              this.removeChar(stringArr, i);
            } else {
              stringWidth += parseFloat(charProduct.width);
            }
          }

          if (stringWidth <= this.variant.space) {
            textArray[index] += stringArr[i];
          }
        }
        // set textArray in store
        this.$store.commit('plate/setTextArray',{index:index, text:textArray[index]});
        this.initializeBundleData();
      },
      initializeBundleData() {
        this.bundleData.textArray = this.getTextArray;
        // set inputErrorArray in store
        this.$store.commit('plate/setInputErrorArray',{index: this.index, errorVal: this.error});
        // emit this.bundleData
        eventBus.$emit('new-data', this.bundleData);
      },
      reInitializeBundleText() {
        this.error = false;
        this.bundleTextArray(this.letterString);
      }
    },
    created() {
      eventBus.$on('new-data', (data) => {
        const text = data.textArray[this.lineOfText - 1];
        this.letterString = text ? text : '';
        this.error = this.checkForErrors();
      })
    }
  };
</script>

<template>
  <div>
    <input
      type="text"
      name="letter-text"
      @keyup.delete="reInitializeBundleText"
      @blur="error = false"
      v-model="letterString"
      :class="error ? 'error' : ''"
      id="letter-text"
      placeholder="TEXT"
    />
    <span v-if="error" data-tip="Plate width limit reached." />
  </div>
</template>

<style scoped>
  [data-tip]:after {
    display: block !important;
  }
  [data-tip]:before {
    display: block !important;
  }
  .error {
    border-color: #c03 !important;
  }
</style>
